//@flow
import apiconfig from "../../../apiconfig";
import { addJRR } from "./actions";
import { addObject, addChildren } from "../../data/actions.js";
import { addSchema, patchSchema } from "../schema/actions.js";
import { schemaCheck } from "../schema";
import store from "../../../store";
import readPath from "../../tiny-functions/readPath";
import getOname from "../../tiny-functions/getOname";
import getSchemaName from "../../tiny-functions/getSchemaName";
import { digestHTTPrespondeCode } from "../../../common/functions/connectivity";
import { ApiUnreachable } from "../error";
import { InvalidToken } from "../error";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/browser";

const SIGNUP_FORM_ENDPOINT = "signupform";

function captureSignupFormMessage(message, extra) {
  try {
    Sentry.withScope(scope => {
      scope.setLevel("error");
      Object.keys(extra).forEach(key => {
        scope.setExtra(key, extra[key]);
      });
      Sentry.captureMessage(message);
    });
  } catch (e) {
    // Never let telemetry break signup.
  }
}

async function captureSignupFormNon200(response, endPoint) {
  if (
    process.env.NODE_ENV !== "production" ||
    !endPoint.includes(SIGNUP_FORM_ENDPOINT)
  ) {
    return;
  }

  if (response == null) {
    captureSignupFormMessage("Signup form API call did not return a response", {
      endPoint
    });
    return;
  }

  if (response.status === 200) {
    return;
  }

  let responseText = "";
  try {
    responseText = await response.clone().text();
  } catch (e) {
    responseText = "Unable to read response body.";
  }

  captureSignupFormMessage("Signup form API call returned non-200", {
    endPoint,
    status: response.status,
    statusText: response.statusText,
    responseText: responseText.slice(0, 2000)
  });
}

export async function fetchApi(
  endPoint,
  method = "get",
  payload = null,
  headers = {},
  jrr = null,
  initOptions = {}
) {
  const storeState = store.getState();

  jrr = jrr != null ? jrr : storeState.services.api.JRR;

  //URL built from config file and specified end point (target)
  let url = `${apiconfig.API.ADDRESS}${endPoint}`;

  //myInit is the second fetch() argument used to specify methods, headers, body...
  let myInit = {
    method: method.toLowerCase(),
    headers: {
      "x-omicron": jrr,
      ...headers
    },
    ...initOptions
  };

  //Body atribute is created only if the method is post. Otherwise it will pop an error
  if (method.toLowerCase() === "post") {
    myInit.body = payload;
  }

  try {
    var res = await fetch(url, myInit);
  } catch (e) {
    return null;
    //throw new ApiUnreachable(e.message);
  }

  if (res.status == 401) {
    store.dispatch(addJRR(""));
  }
  return res;
}

export async function get(endPoint, options = {}, initOptions = {}) {
  //This will check if a schema is avaiable on Redux Store. If yes, send version #, if not, send 0
  let state = store.getState();
  let additionalHttpGetParameters = [];

  for (const property in options.httpGetParameters || {}) {
    additionalHttpGetParameters.push(
      encodeURIComponent(property) +
        "=" +
        encodeURIComponent(options.httpGetParameters[property])
    );
  }

  if (additionalHttpGetParameters.length > 0) {
    additionalHttpGetParameters = "&" + additionalHttpGetParameters.join("&");
  } else {
    additionalHttpGetParameters = "";
  }

  let schemaVersion =
    options.schema != null
      ? options.schema
      : schemaCheck(state.services.schema, getSchemaName(endPoint));
  let completePath = `?target=${endPoint}&schema=${schemaVersion}${additionalHttpGetParameters}`;

  const res = await fetchApi(
    completePath,
    "get",
    null,
    {
      Accept: "application/json"
    },
    null,
    initOptions
  );

  if (res && (await digestHTTPrespondeCode(res, state.services.api.tcount))) {
    const resData = await res.json();

    if ("schema" in resData) {
      store.dispatch(addSchema(resData, endPoint));
    }

    if ("schemaPatch" in resData) {
      store.dispatch(patchSchema(resData.schemaPatch, endPoint));
    }

    return resData;
  }
}

export const post = async function(endPoint, data = {}) {
  //This will check if a schema is avaiable on Redux Store. If yes, send version #, if not, send 0
  let state = store.getState();
  let schemaVersion = schemaCheck(
    state.services.schema,
    getSchemaName(endPoint)
  );
  const response = await fetchApi(
    `?target=${endPoint}&schema=${schemaVersion}`,
    "post",
    JSON.stringify(data),
    {
      Accept: "application/json",
      "Content-Type": "application/json"
    },
    null,
    endPoint.includes(SIGNUP_FORM_ENDPOINT) ? { credentials: "include" } : {}
  );

  await captureSignupFormNon200(response, endPoint);

  if (await digestHTTPrespondeCode(response)) {
    const resData = await response.json();
    if ("JRR" in resData) {
      store.dispatch(addJRR(resData.JRR));
    }

    if ("schema" in resData) {
      store.dispatch(addSchema(resData, endPoint));
    }

    if ("schemaPatch" in resData) {
      store.dispatch(patchSchema(resData.schemaPatch, endPoint));
    }

    return resData;
  }
};

export const put = async function(endPoint, data, headers = {}) {
  //This will check if a schema is avaiable on Redux Store. If yes, send version #, if not, send 0
  let state = store.getState();
  let schemaVersion = schemaCheck(
    state.services.schema,
    getSchemaName(endPoint)
  );

  const response = await fetchApi(
    `?target=${endPoint}&schema=${schemaVersion}&docpost=t`,
    "post",
    data,
    {
      Accept: "application/json",
      ...headers
    }
  );

  if (await digestHTTPrespondeCode(response)) {
    const resData = await response.json();
    if ("JRR" in resData) {
      store.dispatch(addJRR(resData.JRR));
    }

    if ("schema" in resData) {
      store.dispatch(addSchema(resData, endPoint));
    }

    if ("schemaPatch" in resData) {
      store.dispatch(patchSchema(resData.schemaPatch, endPoint));
    }

    return resData;
  }
};

export const del = async function(endPoint) {
  //This will check if a schema is avaiable on Redux Store. If yes, send version #, if not, send 0
  let state = store.getState();
  let schemaVersion = schemaCheck(
    state.services.schema,
    getSchemaName(endPoint)
  );
  const res = await fetchApi(
    `?target=${endPoint}&schema=${schemaVersion}`,
    "delete"
  );

  if (await digestHTTPrespondeCode(res)) {
    const resData = await res.json();
    if ("JRR" in resData) {
      store.dispatch(addJRR(resData.JRR));
    }

    return resData;
  }
};

export const load = async function(endpoint, oName = "") {
  if (oName === "") {
    oName = getOname(endpoint);
  }
  //Api Call
  let response = await get(endpoint);

  if ("schema" in response) {
    store.dispatch(addSchema(response, endpoint));
  }

  if ("schemaPatch" in response) {
    store.dispatch(patchSchema(response.schemaPatch, endpoint));
  }

  let { parentName, parentId } = readPath(endpoint);

  if ("zeroRows" in response) {
    //If object has a parent, put the object's ID on parent properties
    if (parentName != null && parentId != null) {
      store.dispatch(addChildren(parentName, parentId, oName, []));
    }
  } else if ("object" in response) {
    store.dispatch(
      addObject(response.name, response.object, response.object[response.pk])
    );

    /*
    //If object has a parent, put the object's ID on parent properties
    if (parentName != null && parentId != null) {
      if (response.object == null)
        throw new Error(
          "Null object came from API. Object name: " + response.name
        );
      store.dispatch(
        addProperty(parentName, parentId, oName, [response.object[response.pk]])
      );7
    }
    */
  } else if ("list" in response) {
    let ids = [];

    for (let object of response.list) {
      ids.push(object[response.pk]);
      store.dispatch(addObject(response.name, object, object[response.pk]));
    }

    let { parentName, parentId } = readPath(endpoint);

    //Put the list's ID on parent properties
    if (parentName != null && parentId != null) {
      store.dispatch(addChildren(parentName, parentId, oName, ids.slice()));
    }
  } else {
    if (!("schema" in response)) {
      throw new Error(
        "API response don't have either object nor list " +
          endpoint +
          " " +
          oName
      );
    }
  }
};

export const postNload = async function(
  endpoint,
  payload,
  schema,
  ...loadEndpoints
) {
  try {
    var postres = await post(endpoint, payload, schema);
    for (let loadEndpoint of loadEndpoints) {
      if (Array.isArray(loadEndpoint)) {
        await load(loadEndpoint[0], loadEndpoint[1]);
      } else {
        await load(loadEndpoint);
      }
    }
  } catch (e) {
    if (e instanceof InvalidToken) {
      return false;
    } else {
      toast.error("Problem");
      throw e;
    }
  }
  if (postres?.error) {
    toast.error(postres?.message);
  } else {
    toast.success(postres?.message);
  }
  return postres;
};

export const delNload = async function(endpoint, ...loadEndpoints) {
  try {
    await del(endpoint);
    for (let loadEndpoint of loadEndpoints) {
      if (Array.isArray(loadEndpoint)) {
        await load(loadEndpoint[0], loadEndpoint[1]);
      } else {
        await load(loadEndpoint);
      }
    }
  } catch (e) {
    if (e instanceof InvalidToken) {
      return false;
    } else {
      throw e;
    }
  }
  return true;
};
